import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Col, Grid } from '@common/components';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import {
  DashboardSection,
  LayoutFilters,
  NotResults,
} from '@dashboard/components';
import i18n from '@common/config/i18n';
import ResumeCards from '@dashboard/components/ResumeCards/ResumeCards';
import ResumeTimeUsedCard from '@dashboard/components/ResumeCards/ResumeTimeUsedCard';
import { selectFilter, shouldApplyFilters } from '@dashboard/slices/filters';
import { useUsageStatsMutation } from '@dashboard/api/usageStats';
import { selectStats, setStats } from '@dashboard/slices/usageStats';
import { useTranslation, Trans } from 'react-i18next';
import DateFilters from '@dashboard/components/DateFilters/DateFilters';
import { ChartStat } from '@dashboard/components/ChartStat';
import { sortCharts } from '../../util';

import viewClasses from './UsageStatsView.module.scss';

let lastResults = null;
let currentCall = null;

export default function UsageStatsView() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('dashboard\\UsageStatsView');

  const currentStats = useAppSelector(selectStats);
  const currentFilters = useAppSelector(selectFilter);
  const applyFilters = useAppSelector(shouldApplyFilters);

  const [usageStats, { isLoading }] = useUsageStatsMutation();
  const [loading, setLoading] = useState(isLoading);
  const cancelAllRequests = () => {
    if (currentCall) currentCall.abort();
  };

  const filterResults = async () => {
    if (applyFilters) {
      try {
        cancelAllRequests();
        currentCall = usageStats(currentFilters);
        lastResults = await currentCall.unwrap();
        dispatch(setStats(lastResults.data));
      } catch (ex) {
        if (ex && ex.message === 'Aborted') {
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    filterResults();
  }, [applyFilters]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      if (lastResults) {
        setLoading(true);
        setTimeout(() => {
          dispatch(setStats({ ...lastResults.data }));
          setLoading(false);
        });
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  useLayoutEffect(() => {
    filterResults();
    return () => {
      cancelAllRequests();
    };
  }, []);

  return (
    <LayoutFilters>
      <section className={viewClasses.dashboard}>
        <ResumeCards resume={currentStats.resume} />
        <DashboardSection title={t('filtersTitle')}>
          <ResumeTimeUsedCard resume={currentStats.resume} />
          <DateFilters isLoading={loading} />
        </DashboardSection>

        <Grid>
          <Col xss={12} xs={12} sm={12}>
            {!loading &&
            currentStats &&
            currentStats.stats &&
            currentStats.stats.length > 0 ? (
              [...currentStats.stats]
                .sort(sortCharts)
                .map((stat) => (
                  <ChartStat
                    key={stat.name}
                    title={<Trans t={t} i18nKey={stat.name} />}
                    stat={stat}
                  />
                ))
            ) : (
              <NotResults card isLoading={loading} />
            )}
          </Col>
        </Grid>
      </section>
    </LayoutFilters>
  );
}

UsageStatsView.propTypes = {};
